import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Loader2, Box, Smartphone } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import SelectField from "../../components/os/SelectField";
import { input, GrHeader, GrModal } from "./_grShared";

const PLATFORMS = [{ value: "web", label: "Web / model-viewer" }, { value: "ios", label: "iOS (USDZ)" }, { value: "android", label: "Android (Scene Viewer)" }];

export default function GrArViewer() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [f, setF] = useState({ property: "", model_url: "", platform: "web" });

  useEffect(() => {
    if (!document.querySelector("#model-viewer-script")) {
      const s = document.createElement("script");
      s.id = "model-viewer-script"; s.type = "module";
      s.src = "https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js";
      document.head.appendChild(s);
    }
  }, []);
  const load = () => api.get("/gr/ar").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!f.property.trim()) return toast.error("Propiedad requerida");
    try { await api.post("/gr/ar", { ...f, activations: 0 }); setOpen(false); setF({ property: "", model_url: "", platform: "web" }); load(); toast.success("Modelo AR creado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const openView = async (m) => { setView(m); try { const r = await api.post(`/gr/ar/${m.id}/activate`); setItems((p) => p.map((x) => x.id === m.id ? { ...x, activations: r.data.activations } : x)); } catch { /* */ } };
  const del = async (id) => { try { await api.delete(`/gr/ar/${id}`); load(); } catch { /* */ } };

  return (
    <OSLayout title="OMNIA AR Viewer">
      <div className="space-y-6" data-testid="gr-ar-page">
        <div className="flex items-end justify-between gap-3">
          <GrHeader title="OMNIA AR Viewer" desc="Visualiza propiedades y mobiliario en Realidad Aumentada a escala real (modelos glb/USDZ)." />
          <button onClick={() => setOpen(true)} className="btn btn-primary shrink-0" data-testid="gr-ar-new"><Plus className="w-4 h-4" /> Nuevo modelo</button>
        </div>

        {loading ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> :
          items.length === 0 ? <div className="card-surface py-12 text-center text-navy/40 text-sm">Sin modelos AR.</div> :
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gr-ar-grid">
            {items.map((m) => (
              <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-4" data-testid={`gr-ar-${m.id}`}>
                <div className="flex items-start justify-between mb-2"><div className="flex items-center gap-2"><Box className="w-5 h-5 text-violet" /><div><div className="font-medium">{m.property}</div><div className="text-xs text-navy/50">{PLATFORMS.find((p) => p.value === m.platform)?.label}</div></div></div><button onClick={() => del(m.id)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button></div>
                <div className="text-xs text-navy/50 mb-3 inline-flex items-center gap-1"><Smartphone className="w-3.5 h-3.5" /> {m.activations || 0} activaciones</div>
                <button onClick={() => openView(m)} className="btn btn-outline w-full text-sm" data-testid={`gr-ar-view-${m.id}`}><Box className="w-3.5 h-3.5" /> Ver en 3D / AR</button>
              </motion.div>
            ))}
          </div>}
      </div>

      {open && <GrModal title="Nuevo modelo AR" onClose={() => setOpen(false)} testid="gr-ar-dialog">
        <div className="space-y-3">
          <input className={input} placeholder="Propiedad" value={f.property} onChange={(e) => setF({ ...f, property: e.target.value })} data-testid="gr-ar-property" />
          <input className={input} placeholder="URL del modelo (.glb)" value={f.model_url} onChange={(e) => setF({ ...f, model_url: e.target.value })} data-testid="gr-ar-model" />
          <SelectField value={f.platform} options={PLATFORMS} onChange={(v) => setF({ ...f, platform: v })} />
          <p className="text-xs text-navy/40">Usa un archivo .glb accesible por URL. En móviles compatibles se abrirá en AR real.</p>
        </div>
        <div className="flex justify-end gap-2 mt-5"><button onClick={() => setOpen(false)} className="btn btn-ghost">Cancelar</button><button onClick={create} className="btn btn-primary" data-testid="gr-ar-submit">Crear</button></div>
      </GrModal>}

      {view && <GrModal title={view.property} onClose={() => setView(null)} testid="gr-ar-viewer" wide>
        {view.model_url ?
          <model-viewer src={view.model_url} ar camera-controls auto-rotate ar-modes="webxr scene-viewer quick-look" style={{ width: "100%", height: "360px", background: "#f4f4f6", borderRadius: "12px" }} data-testid="gr-ar-modelviewer" />
          : <div className="h-64 rounded-lg bg-navy/5 flex items-center justify-center text-sm text-navy/40">Sin modelo .glb configurado.</div>}
        <p className="text-xs text-navy/40 mt-3">Arrastra para rotar. En móvil compatible aparece el botón "Ver en tu espacio" (AR).</p>
      </GrModal>}
    </OSLayout>
  );
}
